<script lang="ts">
	import {
		Button,
		ButtonType,
		ButtonSize,
		Icon,
		Checkbox,
		CheckboxSize,
		RadioGroup,
		NumberStepper,
		BaseInput,
		TabCase,
		LoadingSpinner
	} from '@repo/commons/components'
	import type { SettingDefinition } from '@repo/commons/settings'
	import { slide } from 'svelte/transition'
	import { t } from 'svelte-i18n'

	interface CategoryNode {
		name: string
		children: CategoryNode[]
		definitions: SettingDefinition[]
	}

	interface AppGroup {
		contribution: { namespace: string; displayName: string; toggleAllCategories?: string[] }
		tree: CategoryNode[]
	}

	interface Props {
		tabs: { id: string; name: string; isActive: boolean }[]
		searchQuery: string
		isRefetching: boolean
		isInitialLoading: boolean
		groupedByApp: AppGroup[]
		getDraftValue: (key: string) => unknown
		setDraftValue: (key: string, value: unknown) => void
		isDraftValueLocked: (key: string) => boolean
		onTabChange: (id: string) => void
		onSearchInput: (value: string) => void
		onSave: () => void
		onCancel: () => void
	}

	let {
		tabs,
		searchQuery,
		isRefetching,
		isInitialLoading,
		groupedByApp,
		getDraftValue,
		setDraftValue,
		isDraftValueLocked,
		onTabChange,
		onSearchInput,
		onSave,
		onCancel
	}: Props = $props()

	let collapsedSections = $state<Record<string, boolean>>({})

	function isCollapsed(key: string): boolean {
		return collapsedSections[key] ?? false
	}

	function toggleCollapsed(key: string): void {
		collapsedSections = { ...collapsedSections, [key]: !isCollapsed(key) }
	}
</script>

<div class="view-settings">
	<TabCase
		tabs={tabs.map((tab) => ({ ...tab, name: $t(tab.name) }))}
		onpress={onTabChange}
		disabled={!!searchQuery}
	/>

	<div class="search-wrapper">
		<BaseInput
			size="md"
			placeholder={$t('ViewSettings.search')}
			value={searchQuery}
			clearable
			onInput={onSearchInput}
			prepend={{
				component: Icon,
				props: { name: 'search', class: 'text-[#002C4D80]' }
			}}
			append={isRefetching
				? { component: LoadingSpinner, props: { size: 16, color: '#8aa0b4' } }
				: null}
		/>
	</div>

	{#snippet renderSettings(definitions: SettingDefinition[])}
		{#each definitions as def}
			{@const title = def.title ? $t(def.title) : ''}
			{@const description = def.description ? $t(def.description) : undefined}
			<div class="setting-item">
				{#if def.type === 'boolean'}
					<Checkbox
						label={title}
						hint={description}
						size={CheckboxSize.SMALL}
						checked={getDraftValue(def.key) === true}
						disabled={isDraftValueLocked(def.key)}
						onchange={(checked) => setDraftValue(def.key, checked)}
					/>
				{:else if def.type === 'enum'}
					<RadioGroup
						name={def.key}
						legend={title}
						size="small"
						tooltip={description ? { text: description } : undefined}
						value={getDraftValue(def.key)}
						options={(def.enumValues ?? []).map((value, i) => ({
							value,
							label: def.enumLabels?.[i] ? $t(def.enumLabels[i]) : value
						}))}
						onchange={(value) => setDraftValue(def.key, value)}
					/>
				{:else if def.type === 'number'}
					<NumberStepper
						label={title}
						size="md"
						bind:value={() => getDraftValue(def.key) as number, (v) => setDraftValue(def.key, v)}
						minValue={def.minimum ?? 0}
						maxValue={def.maximum ?? 1000000}
					/>
				{:else if def.type === 'string'}
					<BaseInput
						label={title}
						size="md"
						value={getDraftValue(def.key) as string}
						onInput={(value) => setDraftValue(def.key, value)}
					/>
				{/if}
			</div>
		{/each}
	{/snippet}

	{#snippet renderNode(node: CategoryNode, parentPath: string, toggleAllCategories: string[])}
		{@const nodePath = `${parentPath}/${node.name}`}
		{#if node.children.length > 0}
			<div class="settings-group">
				<button class="group-header" onclick={() => toggleCollapsed(nodePath)}>
					<span class="group-title">{$t(node.name)}</span>
					<Icon
						name="chevronRight"
						size="sm"
						class="chevron {isCollapsed(nodePath) ? '' : 'expanded'}"
					/>
				</button>

				{#if !isCollapsed(nodePath)}
					<div class="group-content" transition:slide={{ duration: 200 }}>
						{@render renderSettings(node.definitions)}

						{#each node.children as child}
							{@render renderNode(child, nodePath, toggleAllCategories)}
						{/each}
					</div>
				{/if}
			</div>
		{:else}
			{@const hasToggleAll = toggleAllCategories.includes(node.name)}
			{@const allChecked =
				hasToggleAll && node.definitions.every((d) => getDraftValue(d.key) === true)}
			{@const someChecked =
				hasToggleAll && node.definitions.some((d) => getDraftValue(d.key) === true)}
			<div class="leaf-group">
				<span class="leaf-title">{$t(node.name)}</span>
				{#if hasToggleAll}
					<div class="setting-item">
						<Checkbox
							label={$t('ViewSettings.toggleAll')}
							size={CheckboxSize.SMALL}
							checked={allChecked}
							indeterminate={someChecked && !allChecked}
							onchange={(checked) => {
								for (const d of node.definitions) {
									setDraftValue(d.key, checked)
								}
							}}
						/>
					</div>
				{/if}
				<div class="leaf-settings" class:toggle-all-options={hasToggleAll}>
					{@render renderSettings(node.definitions)}
				</div>
			</div>
		{/if}
	{/snippet}

	<div class="settings-body">
		{#if isInitialLoading}
			<div class="loading-state">
				<LoadingSpinner size={32} color="#8aa0b4" />
			</div>
		{:else if groupedByApp.length === 0}
			<div class="empty-state">{$t('ViewSettings.empty')}</div>
		{:else}
			<div class="settings-results" class:is-refetching={isRefetching}>
				{#each groupedByApp as { contribution, tree }}
					{@const appKey = `app/${contribution.namespace}`}
					<div class="app-group">
						<button class="app-group-header" onclick={() => toggleCollapsed(appKey)}>
							<span class="app-group-title">{$t(contribution.displayName)}</span>
							<Icon
								name="chevronRight"
								size="sm"
								class="chevron {isCollapsed(appKey) ? '' : 'expanded'}"
							/>
						</button>

						{#if !isCollapsed(appKey)}
							<div class="settings-group-container" transition:slide={{ duration: 200 }}>
								{#each tree as node}
									{@render renderNode(node, appKey, contribution.toggleAllCategories ?? [])}
								{/each}
							</div>
						{/if}
					</div>
				{/each}
			</div>
		{/if}
	</div>

	<div class="settings-footer">
		<Button
			buttonText={$t('ViewSettings.save')}
			buttonType={ButtonType.BRAND}
			buttonSize={ButtonSize.LARGE}
			onclick={onSave}
		/>
		<Button
			buttonText={$t('ViewSettings.cancel')}
			buttonType={ButtonType.TERTIARY}
			buttonSize={ButtonSize.LARGE}
			onclick={onCancel}
		/>
	</div>
</div>

<style lang="postcss">
	.view-settings {
		@apply flex flex-col gap-4;
		min-width: 700px;
	}

	.search-wrapper {
		@apply w-full;
	}

	.settings-body {
		@apply flex flex-col gap-4 overflow-y-auto;
		max-height: 50vh;
	}

	.settings-results {
		@apply flex flex-col gap-4;
		transition: opacity 0.15s ease;
	}

	.settings-results.is-refetching {
		opacity: 0.5;
		pointer-events: none;
	}

	.loading-state {
		@apply flex items-center justify-center py-12;
	}

	.empty-state {
		@apply flex items-center justify-center py-12 text-sm text-gray-500;
	}

	.app-group {
		@apply flex flex-col rounded-xl border border-dashed border-gray-300 p-4;
	}

	.app-group-header {
		@apply flex items-center justify-between cursor-pointer border-none bg-transparent p-0 w-full text-left;
	}

	.app-group-title {
		@apply text-base font-semibold text-gray-900;
	}

	:global(.chevron) {
		transition: transform 0.2s ease;
	}

	:global(.chevron.expanded) {
		transform: rotate(90deg);
	}

	.settings-group-container {
		@apply pt-3;
	}

	.settings-group-container .settings-group:not(:last-child) {
		@apply mb-4;
	}

	.settings-group {
		@apply flex flex-col rounded-xl border border-gray-400 p-4;
	}

	.leaf-group {
		@apply flex flex-col gap-2 mt-4;
	}

	.leaf-settings {
		@apply flex flex-col gap-2;
	}

	.toggle-all-options {
		@apply border-t border-[#002C4D]/10 pt-3;
	}

	.leaf-title {
		@apply text-sm font-medium text-gray-700;
	}

	.group-header {
		@apply flex items-center justify-between cursor-pointer border-none bg-transparent p-0 w-full text-left;
	}

	.group-title {
		@apply text-sm font-semibold text-gray-900;
	}

	.group-content {
		@apply flex flex-col gap-2 pt-3;
	}

	.setting-item {
		@apply flex flex-col;
	}

	.settings-footer {
		@apply flex items-center gap-4 pt-2;
	}
</style>
